import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import { FaArrowRight, FaBoxOpen, FaPhoneAlt, FaSearchLocation, FaUser } from 'react-icons/fa';
import PlaceAutocompleteInput from './PlaceAutocompleteInput';
import heroImage from '../assets/hero.jpg';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const PHONE_PREFIX_BY_COUNTRY = { Benin: '+229', Togo: '+228' };

const emptyPoint = { value: '', place: null, contactName: '', phone: '' };

const HeroSection = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('ship'); // 'ship' | 'track'
  const country = 'Benin'; // Un seul pays desservi : Bénin (plus de sélecteur)
  const [pickup, setPickup] = useState(emptyPoint);
  const [dropoff, setDropoff] = useState(emptyPoint);
  const [trackId, setTrackId] = useState('');

  const inputsDisabled = false; // Bénin figé : champs actifs d'emblée
  const phonePrefix = PHONE_PREFIX_BY_COUNTRY[country] || '+229';
  const sanitizePhone = (raw) => raw.replace(/[^\d\s]/g, '');

  // Expédier : on transmet pays + points (coordonnées + contacts) à /quick-delivery.
  const handleShip = (e) => {
    e.preventDefault();
    navigate('/quick-delivery', {
      state: {
        country: country || undefined,
        pickup: { value: pickup.value.trim(), place: pickup.place, contactName: pickup.contactName, phone: pickup.phone },
        dropoff: { value: dropoff.value.trim(), place: dropoff.place, contactName: dropoff.contactName, phone: dropoff.phone },
      },
    });
  };

  // Champs contact (nom + téléphone) d'un point — même logique que /quick-delivery.
  const renderContact = (point, setter, namePlaceholder) => (
    <div className="grid grid-cols-2 gap-2">
      <div className="relative">
        <FaUser className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
        <input
          type="text"
          value={point.contactName}
          disabled={inputsDisabled}
          onChange={(e) => setter((p) => ({ ...p, contactName: e.target.value }))}
          placeholder={namePlaceholder}
          className="w-full bg-gray-100 rounded-lg py-2.5 pl-9 pr-3 text-sm text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-custom disabled:cursor-not-allowed disabled:opacity-60"
        />
      </div>
      <div className="relative">
        <FaPhoneAlt className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
        <span className="absolute left-8 top-1/2 -translate-y-1/2 text-xs text-gray-500 font-medium pointer-events-none">{phonePrefix}</span>
        <input
          type="tel"
          value={point.phone}
          disabled={inputsDisabled}
          onChange={(e) => setter((p) => ({ ...p, phone: sanitizePhone(e.target.value) }))}
          placeholder="Téléphone"
          className="w-full bg-gray-100 rounded-lg py-2.5 pl-[4.7rem] pr-3 text-sm text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-custom disabled:cursor-not-allowed disabled:opacity-60"
        />
      </div>
    </div>
  );

  // Suivre : redirige vers /tracking avec auto-soumission.
  const handleTrack = (e) => {
    e.preventDefault();
    const id = trackId.trim();
    navigate(id ? `/tracking?tracking-id=${encodeURIComponent(id)}&submit=1` : '/tracking');
  };

  const tabBtn = (key) =>
    `flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
      tab === key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'
    }`;

  const placeInputClass =
    'w-full bg-gray-100 rounded-lg py-3 pl-12 pr-10 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-custom transition-all disabled:cursor-not-allowed disabled:opacity-60';
  const fieldClass =
    'w-full bg-gray-100 rounded-lg py-3 pl-11 pr-4 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-custom transition-all';

  // Skeleton affiché le temps que Google Maps charge (le reste du hero reste visible).
  const widgetSkeleton = (
    <div className="w-full max-w-xl bg-white rounded-2xl shadow-xl border border-gray-100 p-3">
      <div className="h-11 bg-gray-100 rounded-xl mb-3 animate-pulse" />
      <div className="space-y-2.5 p-1">
        <div className="h-11 bg-gray-100 rounded-lg animate-pulse" />
        <div className="h-11 bg-gray-100 rounded-lg animate-pulse" />
        <div className="h-11 bg-gray-100 rounded-lg animate-pulse" />
      </div>
    </div>
  );

  const widget = (
    <div className="w-full max-w-xl bg-white rounded-2xl shadow-xl border border-gray-100 p-4">
      <div className="flex p-1 bg-gray-100 rounded-xl mb-3">
        <button type="button" onClick={() => setTab('ship')} className={tabBtn('ship')}>
          Expédier un colis
        </button>
        <button type="button" onClick={() => setTab('track')} className={tabBtn('track')}>
          Suivre un colis
        </button>
      </div>

      {tab === 'ship' ? (
        <form onSubmit={handleShip} className="p-1 space-y-2.5">
          {/* Point de départ + expéditeur */}
          <div className="space-y-2">
            <PlaceAutocompleteInput
              value={pickup.value}
              onChange={(v) => setPickup((p) => ({ ...p, value: v }))}
              onPlaceSelect={(place) => setPickup((p) => ({ ...p, place, value: place.name || place.address || p.value }))}
              placeholder="Point de départ"
              country={country}
              icon={<div className="w-2.5 h-2.5 rounded-full bg-gray-900" />}
              inputClassName={placeInputClass}
              disabled={inputsDisabled}
            />
            {renderContact(pickup, setPickup, "Nom de l'expéditeur")}
          </div>

          {/* Point d'arrivée + destinataire */}
          <div className="space-y-2">
            <PlaceAutocompleteInput
              value={dropoff.value}
              onChange={(v) => setDropoff((p) => ({ ...p, value: v }))}
              onPlaceSelect={(place) => setDropoff((p) => ({ ...p, place, value: place.name || place.address || p.value }))}
              placeholder="Point d'arrivée"
              country={country}
              icon={<div className="w-2.5 h-2.5 bg-primary-custom" />}
              inputClassName={placeInputClass}
              disabled={inputsDisabled}
            />
            {renderContact(dropoff, setDropoff, 'Nom du destinataire')}
          </div>

          <button
            type="submit"
            className="w-full bg-primary-custom text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-custom-dark transition-colors flex items-center justify-center gap-2"
          >
            <FaBoxOpen className="w-4 h-4" /> Expédier maintenant <FaArrowRight className="w-4 h-4" />
          </button>
          <p className="text-xs text-gray-400 text-center pt-0.5">Sans créer de compte · devis instantané</p>
        </form>
      ) : (
        <form onSubmit={handleTrack} className="p-1 space-y-2.5">
          <div className="relative">
            <FaSearchLocation className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={trackId}
              onChange={(e) => setTrackId(e.target.value)}
              placeholder="N° de suivi (ex. OGB1282690700)"
              className={fieldClass}
            />
          </div>
          <button
            type="submit"
            className="w-full bg-gray-900 text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
          >
            Suivre mon colis <FaArrowRight className="w-4 h-4" />
          </button>
          <p className="text-xs text-gray-400 text-center pt-0.5">Entrez le numéro reçu à la commande</p>
        </form>
      )}
    </div>
  );

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-orange-50 via-white to-orange-50 text-gray-800 py-16 lg:py-24">
      {/* Decorative Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-40">
        <div className="absolute -top-[10%] -left-[10%] w-[40%] h-[40%] bg-orange-200 rounded-full blur-[100px]" />
        <div className="absolute top-[20%] right-[5%] w-[30%] h-[30%] bg-blue-100 rounded-full blur-[100px]" />
      </div>

      <div className="container mx-auto">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-8">
          {/* Left column — pas d'animate-on-scroll ici : son transform casserait le
              position:fixed du picker carte (le modal se recalerait sur cette colonne). */}
          <div className="lg:w-1/2 2xl:w-[55%] flex flex-col justify-center items-center text-center lg:items-start lg:text-left">
            <h1 className="text-4xl lg:text-6xl font-extrabold mb-5 leading-tight text-gray-900">
              Expédiez tout, <span className="text-primary-custom">partout</span>.
            </h1>
            <p className="text-gray-500 mb-7 font-inter max-w-xl">
              Envoyez un colis en quelques clics et suivez-le en temps réel, du départ jusqu'à la livraison.
            </p>

            {/* Widget Expédier / Suivre — enveloppé dans le Wrapper Google Maps pour l'autocomplete */}
            <Wrapper
              apiKey={GOOGLE_MAPS_API_KEY}
              libraries={['places', 'marker']}
              render={(status) => (status === Status.LOADING ? widgetSkeleton : widget)}
            />

            {/* Lien secondaire marchands */}
            <a
              href="/marchands"
              className="mt-5 text-sm font-medium text-gray-600 hover:text-gray-900 inline-flex items-center gap-1.5"
            >
              Vous êtes un marchand ? Découvrez nos solutions logistiques <FaArrowRight className="w-3 h-3" />
            </a>
          </div>

          {/* Right column - image */}
          <div className="w-full lg:w-1/2 2xl:w-[45%] animate-on-scroll">
            <div className="h-[320px] lg:h-[540px] w-full overflow-hidden rounded-2xl border-8 border-orange-200/30">
              <img src={heroImage} alt="Réseau de livraison OuiGoo" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
